import AdminLogin from '@/components/AdminLogin';
import Alert from '@/components/Alert';
import Layout from '@/components/Layout';
import ToggleSwitch from '@/components/ToggleSwitch';
import UploadInput from '@/components/UploadInput';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { api } from '@/lib/apiClient';
import React, { useState } from 'react';

const Admin = () => {
    const [isAdmin, setIsAdmin] = useState(false);
    const [password, setPassword] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(true);

    const [albumTitle, setAlbumTitle] = useState('');
    const [albumDescription, setAlbumDescription] = useState('');
    const [isFeatured, setIsFeatured] = useState(false);
    const [isFree, setIsFree] = useState(false);
    const [uploadedPhotos, setUploadedPhotos] = useState<File[]>([]);
    const [coverPhotoIndex, setCoverPhotoIndex] = useState<number | null>(null);
    const [popupMessage, setPopupMessage] = useState('');
    const [showPopup, setShowPopup] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    const ADMIN_PASSWORD = import.meta.env.VITE_ADMIN_PASSWORD || 'admin123';

    const handlePasswordSubmit = () => {
        if (password === ADMIN_PASSWORD) {
            setIsAdmin(true);
            setIsModalOpen(false);
        } else {
            setPopupMessage('Incorrect password. Please try again.');
            setShowPopup(true);
        }
    };

    const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            setUploadedPhotos([...uploadedPhotos, ...Array.from(files)]);
            setPopupMessage(`${files.length} file(s) selected for upload.`);
            setShowPopup(true);
        }
    };

    const handleCoverPhotoChange = (index: number) => {
        setCoverPhotoIndex(index);
    };

    const handleSaveAlbum = async () => {
        try {
            setPopupMessage('Uploading... Please wait.');
            setShowPopup(true);
            setIsUploading(true);

            const formData = new FormData();
            formData.append('title', albumTitle);
            formData.append('description', albumDescription);
            formData.append('isFeatured', isFeatured.toString());
            formData.append('isFree', isFree.toString());

            // Append photos and metadata
            uploadedPhotos.forEach((file, index) => {
                formData.append('photos', file); // Add photo file
                formData.append(
                    `metadata-${index}`,
                    JSON.stringify({ isCover: index === coverPhotoIndex })
                );
            });

            const response = await api.albums.create(formData);

            setIsUploading(false);

            const data = response.data;
            setPopupMessage(`Album "${data.title}" saved successfully!`);
            setShowPopup(true);

            // Reset state
            setUploadedPhotos([]);
            setAlbumTitle('');
            setAlbumDescription('');
            setIsFeatured(false);
            setIsFree(false);
            setCoverPhotoIndex(null);
        } catch (error) {
            console.error('Error saving album:', error);
            setPopupMessage('Failed to save album. Please try again.');
            setShowPopup(true);
            setIsUploading(false);
        }
    };

    return (
        <Layout>
            <div className="flex items-center justify-center p-6 sm:p-8 md:p-12">
                {isModalOpen && (
                    <Dialog>
                        <AdminLogin
                            password={password}
                            onPasswordChange={(e) => setPassword(e.target.value)}
                            onSubmit={handlePasswordSubmit}
                        />
                    </Dialog>
                )}

                {isAdmin && (
                    <div>
                        <div className="p-4 sm:p-12 border border-gray-700 rounded-xl shadow-xl">
                            <div className="rounded space-y-6">
                                <h1 className="text-3xl sm:text-5xl font-extrabold mb-6">Admin Panel</h1>

                                {/* Album Title */}
                                <label className="block">
                                    <span className="block text-lg font-semibold mb-2">
                                        Album Title
                                    </span>
                                    <Input
                                        type="text"
                                        value={albumTitle}
                                        onChange={(e) => setAlbumTitle(e.target.value)}
                                        placeholder="Enter album title"
                                        className="w-full p-2 border border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-gray-500"
                                    />
                                </label>

                                {/* Album Description */}
                                <label className="block">
                                    <span className="block text-lg font-semibold mb-2">
                                        Album Description
                                    </span>
                                    <Input
                                        value={albumDescription}
                                        onChange={(e) => setAlbumDescription(e.target.value)}
                                        placeholder="Enter album description"
                                        className="w-full p-2 border border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-gray-500"
                                    />
                                </label>

                                {/* Is Featured */}
                                <ToggleSwitch
                                    label="Is Featured"
                                    isChecked={isFeatured}
                                    onToggle={() => setIsFeatured(!isFeatured)}
                                />

                                {/* Is Free */}
                                <ToggleSwitch
                                    label="Is Free"
                                    isChecked={isFree}
                                    onToggle={() => setIsFree(!isFree)}
                                />

                                {/* Upload Photos */}
                                <UploadInput label="Upload Photos" onChange={handlePhotoUpload} />

                                {/* Photo Previews */}
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-4 mt-6">
                                    {uploadedPhotos.map((photo, index) => (
                                        <div
                                            key={index}
                                            className={`relative border rounded-md overflow-hidden shadow-md ${coverPhotoIndex === index ? 'border-gray-900 ring-1 ring-gray-800' : 'border-gray-700'
                                                }`}
                                        >
                                            {/* Photo Thumbnail */}
                                            <img
                                                src={URL.createObjectURL(photo)}
                                                alt={`Photo ${index + 1}`}
                                                className="w-full h-32 object-cover"
                                            />

                                            {/* Cover Photo Toggle */}
                                            <div
                                                className={`absolute inset-x-0 bottom-0 px-4 py-3 flex justify-between items-center rounded-b-lg shadow-md ${coverPhotoIndex === index ? 'bg-blue-900' : 'bg-gray-800'
                                                    }`}
                                            >
                                                <span
                                                    className={`text-sm font-medium ${coverPhotoIndex === index ? 'text-gray-50' : 'text-gray-400'
                                                        }`}
                                                >
                                                    {coverPhotoIndex === index ? 'Cover Photo' : 'Set as Cover'}
                                                </span>
                                                <label className="relative inline-flex items-center cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={coverPhotoIndex === index}
                                                        onChange={() => handleCoverPhotoChange(index)}
                                                        className="sr-only peer"
                                                        title={coverPhotoIndex === index ? 'Unset' : 'Set'}
                                                    />
                                                    <div
                                                        className={`w-10 h-5 bg-gray-700 rounded-full peer peer-focus:ring-2 peer-focus:ring-blue-500 transition-all ${coverPhotoIndex === index ? 'peer-checked:bg-gray-500' : ''
                                                            }`}
                                                    ></div>
                                                    <div
                                                        className={`absolute left-0.5 top-0.5 w-4 h-4 bg-gray-50 rounded-full transition-transform ${coverPhotoIndex === index ? 'translate-x-5' : 'translate-x-0'
                                                            }`}
                                                    ></div>
                                                </label>
                                            </div>

                                        </div>
                                    ))}
                                </div>


                                {/* Save Button */}
                                <Button
                                    onClick={handleSaveAlbum}
                                    className="w-full bg-gray-950 py-2 rounded hover:bg-gray-800"
                                    disabled={isUploading}
                                >
                                    {isUploading ? 'Uploading...' : 'Save Album'}
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Alert Popup */}
                <Alert
                    message={popupMessage}
                    onClose={() => setShowPopup(false)}
                    isVisible={showPopup}
                />
            </div>
        </Layout>
    );
};

export default Admin;
